import { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { Responsive } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { VisualRenderer } from '../visuals/VisualRenderer';
import { Button } from '@/components/ui/button';
import { Layout, Grid3X3, Eye, Download } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useTimezoneFormatter } from '@/hooks/useTimezoneFormatter';
import { useTenantExportBranding } from '@/hooks/useTenantExportBranding';
import { ExportBrandingHeader, waitForBrandingImages } from '../../common/ExportBrandingHeader';
import BRAND from '@/config/branding';


const ResponsiveGridLayout = Responsive;

const A4_WIDTH_LANDSCAPE = 1200;

interface DashboardGridProps {
    visualNodes: any[];
    flowId: string;
    onNodeUpdate?: (nodeId: string, data: any) => void;
}

export default function DashboardGrid({ visualNodes, flowId, onNodeUpdate }: DashboardGridProps) {
    const { formatDate } = useTimezoneFormatter();
    const tenantBranding = useTenantExportBranding();

    const [layout, setLayout] = useState<any[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    // Remove local visualNodes state as it's now a prop
    const [showPreview, setShowPreview] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    useEffect(() => {
        // visualNodes is now passed directly
        const currentLayout = visualNodes.map((node, index) => {
            if (node.data?.dashboardLayout) {
                return {
                    i: node.id,
                    ...node.data.dashboardLayout
                };
            }
            // Smart layout defaults
            return {
                i: node.id,
                x: (index % 3) * 4,
                y: Math.floor(index / 3) * 4,
                w: 4,
                h: 4,
                minW: 2,
                minH: 2
            };
        });
        setLayout(currentLayout);
    }, [visualNodes]);

    const handleLayoutChange = (currentLayout: any[]) => {
        setLayout(currentLayout);
        if (onNodeUpdate) {
            currentLayout.forEach(l => {
                onNodeUpdate(l.i, {
                    dashboardLayout: { x: l.x, y: l.y, w: l.w, h: l.h, minW: l.minW, minH: l.minH }
                });
            });
        }
    };

    const generatePdfDocument = async () => {
        if (!containerRef.current) return null;

        try {
            // 1. Clone the node to render it "off-screen" but in full view
            const originalElement = containerRef.current;
            const clone = originalElement.cloneNode(true) as HTMLElement;

            // 2. Style the clone to be perfect for capture
            // We force it to be visible, top-left, full width, and explicitly sized height
            const fullHeight = originalElement.scrollHeight;
            clone.style.position = 'fixed';
            clone.style.top = '0';
            clone.style.left = '-5000px'; // Move off-screen safely
            clone.style.width = `${A4_WIDTH_LANDSCAPE}px`;
            clone.style.height = `${fullHeight}px`;
            clone.style.zIndex = '9999'; // Positive Z to ensure render
            clone.style.overflow = 'visible';
            clone.style.transform = 'none'; // Remove any potential transforms
            clone.style.backgroundColor = '#ffffff'; // Ensure white bg

            // Remove "drag handles" from clone for clean look?
            // Optional, but might be nice.
            // For now, we leave them as they provide structure, or we could set opacity 0 via CSS injection?

            document.body.appendChild(clone);

            // 3. Wait a moment for images/fonts in clone to settle (if any)
            await waitForBrandingImages(clone);
            await new Promise(r => setTimeout(r, 800));

            // 4. Capture
            const canvas = await html2canvas(clone, {
                scale: 1.5, // 1.5 is enough for quality, 2 might be too heavy causing timeouts
                useCORS: true,
                backgroundColor: '#ffffff',
                logging: false,
                width: A4_WIDTH_LANDSCAPE,
                height: fullHeight,
                windowWidth: A4_WIDTH_LANDSCAPE,
                windowHeight: fullHeight
            });

            // 5. Cleanup
            document.body.removeChild(clone);

            // 6. Generate PDF (Slicing logic)
            const pdf = new jsPDF('l', 'mm', 'a4');
            const pdfPageWidth = pdf.internal.pageSize.getWidth();
            const pdfPageHeight = pdf.internal.pageSize.getHeight();

            const imgWidth = canvas.width;
            const imgHeight = canvas.height;
            const ratio = imgWidth / pdfPageWidth;
            const pageHeightInPixels = pdfPageHeight * ratio;

            let heightLeft = imgHeight;
            let currentY = 0;

            while (heightLeft > 0) {
                if (currentY > 0) pdf.addPage();
                const sliceHeight = Math.min(pageHeightInPixels, heightLeft);

                const pageCanvas = document.createElement('canvas');
                pageCanvas.width = imgWidth;
                pageCanvas.height = sliceHeight;

                const ctx = pageCanvas.getContext('2d');
                if (ctx) {
                    ctx.fillStyle = '#ffffff';
                    ctx.fillRect(0, 0, imgWidth, sliceHeight);
                    ctx.drawImage(canvas, 0, currentY, imgWidth, sliceHeight, 0, 0, imgWidth, sliceHeight);

                    const imgData = pageCanvas.toDataURL('image/jpeg', 0.90); // High quality JPEG
                    const pdfSliceHeight = sliceHeight / ratio;
                    pdf.addImage(imgData, 'JPEG', 0, 0, pdfPageWidth, pdfSliceHeight);
                }
                heightLeft -= sliceHeight;
                currentY += sliceHeight;
            }

            return pdf;
        } catch (err) {
            console.error(err);
            return null;
        }
    };

    const handleDownloadPdf = async () => {
        const toastId = toast.loading("Generating PDF...");
        const doc = await generatePdfDocument();
        if (doc) {
            doc.save(`dashboard-${dayjs().format('YYYY-MM-DD')}.pdf`);
            toast.success("PDF Downloaded!");
        } else {
            toast.error("Failed to generate PDF");
        }
        toast.dismiss(toastId);
    };

    const handlePreviewPdf = async () => {
        const toastId = toast.loading("Generating Preview...");
        const doc = await generatePdfDocument();
        if (doc) {
            const blobUrl = doc.output('bloburl');
            setPreviewUrl(blobUrl.toString());
            setShowPreview(true);
        } else {
            toast.error("Failed to generate Preview");
        }
        toast.dismiss(toastId);
    };

    if (visualNodes.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 bg-slate-50/50">
                <div className="p-6 bg-white rounded-full shadow-sm mb-4">
                    <Layout className="w-12 h-12 text-slate-300" />
                </div>
                <h3 className="text-xl font-medium text-slate-700">Empty Dashboard</h3>
                <p className="text-sm text-slate-500 mt-2">Connect "Visual" nodes in your flow to visualize data here.</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-slate-50 overflow-hidden font-sans">
            {/* Premium Toolbar */}
            <div className="h-16 border-b border-slate-200 bg-white px-6 flex items-center justify-between shadow-sm z-20 shrink-0">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-600 rounded-lg text-white shadow-lg shadow-blue-600/20">
                        <Grid3X3 className="w-5 h-5" />
                    </div>
                    <div>
                        <h2 className="font-bold text-slate-800 text-sm leading-tight">Analytics Dashboard</h2>
                        <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Design Mode</div>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePreviewPdf}
                        className="gap-2 text-slate-600 border-slate-200 hover:bg-slate-50"
                    >
                        <Eye className="w-4 h-4" />
                        Preview
                    </Button>
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleDownloadPdf}
                        className="gap-2 bg-slate-900 hover:bg-slate-800 text-white border-0 shadow-md"
                    >
                        <Download className="w-4 h-4" />
                        Download PDF
                    </Button>
                </div>
            </div>

            {/* Canvas Area with Dot Pattern */}
            <div className="flex-1 overflow-y-auto p-8 relative" id="dashboard-scroll-area">
                {/* Background Pattern */}
                <div className="absolute inset-0 z-0 opacity-[0.4]"
                    style={{ backgroundImage: 'radial-gradient(#cbd5e1 1px, transparent 1px)', backgroundSize: '20px 20px' }}>
                </div>

                <div className="flex justify-center min-h-full relative z-10 pb-20">

                    {/* The A4 Canvas Paper */}
                    <div
                        ref={containerRef}
                        className="bg-white shadow-2xl ring-1 ring-slate-900/5 transition-all mx-auto"
                        style={{
                            width: `${A4_WIDTH_LANDSCAPE}px`,
                            minHeight: '800px',
                            padding: '40px'
                        }}
                    >
                        {/* Elegant Header — Uses shared ExportBrandingHeader */}
                        <ExportBrandingHeader
                            title="Strategy Report"
                            meta="Dashboard Overview"
                            sx={{ mb: 6, pb: 4, borderBottom: '1px solid #f1f5f9' }}
                        >
                            <div className="mb-4">
                                <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Strategy Report</h1>
                                <p className="text-slate-500 mt-2 font-medium">Dashboard Overview</p>
                            </div>

                            <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-4">
                                Generated {formatDate(dayjs())}
                            </div>
                        </ExportBrandingHeader>

                        <ResponsiveGridLayout
                            className="layout"
                            width={A4_WIDTH_LANDSCAPE}
                            layouts={{ lg: layout }}
                            breakpoints={{ lg: 1150, md: 900, sm: 600, xs: 400, xxs: 0 }}
                            cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                            rowHeight={100}
                            onLayoutChange={handleLayoutChange}
                            isDraggable={true}
                            isResizable={true}
                            draggableHandle=".drag-handle"
                            margin={[24, 24]}
                        >
                            {visualNodes.map((node) => {
                                return (
                                    <div key={node.id} className="bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 flex flex-col group overflow-hidden ring-0 hover:ring-2 hover:ring-blue-500/10">
                                        <div className="drag-handle h-8 bg-white border-b border-slate-100 flex items-center px-3 cursor-move justify-between group-hover:bg-slate-50/50 transition-colors">
                                            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider truncate flex items-center gap-2">
                                                <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                                                {node.data?.label || 'Chart Component'}
                                            </span>
                                        </div>
                                        <div className="flex-1 p-4 relative overflow-hidden">
                                            {node.data?.previewData ? (
                                                <VisualRenderer
                                                    type={node.data.visualType}
                                                    data={node.data.previewData}
                                                    xAxisKey={node.data.xAxis}
                                                    yAxisKey={node.data.yAxis}
                                                    color={node.data.color}
                                                    showGrid={node.data.showGrid}
                                                    groupKey={node.data.group}
                                                    showLegend={node.data.showLegend}
                                                    formatSettings={{
                                                        formatType: node.data.formatType,
                                                        currency: node.data.currency,
                                                        decimals: node.data.decimals,
                                                        units: node.data.units
                                                    }}
                                                />
                                            ) : (
                                                <div className="flex items-center justify-center h-full flex-col gap-2">
                                                    <div className="w-8 h-8 rounded-full border-2 border-slate-100 border-t-blue-500 animate-spin"></div>
                                                    <span className="text-[10px] font-medium text-slate-400">Loading Data...</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </ResponsiveGridLayout>

                        <div className="mt-12 pt-6 border-t border-slate-100 flex justify-between items-center text-xs text-slate-400 font-medium">
                            <span>Confidential - Internal Use Only</span>
                            <span className="flex items-center gap-1">
                                powered by <span className="text-blue-600 font-bold">{BRAND.poweredByLabel}</span> | {BRAND.poweredByUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Preview Modal */}
            <Dialog open={showPreview} onOpenChange={setShowPreview}>
                <DialogContent className="max-w-4xl w-[90vw] h-[90vh] p-0 overflow-hidden bg-slate-100">
                    <DialogHeader className="px-6 py-4 bg-white border-b shadow-sm flex-shrink-0">
                        <DialogTitle className="flex items-center gap-2">
                            <Eye className="w-5 h-5 text-blue-600" />
                            Print Preview
                        </DialogTitle>
                    </DialogHeader>
                    <div className="flex-1 w-full h-full bg-slate-500 p-4 overflow-y-auto flex justify-center">
                        {previewUrl && (
                            <iframe
                                src={previewUrl}
                                className="w-full h-full bg-white shadow-2xl rounded-sm"
                                style={{ maxWidth: '297mm', height: '100%', aspectRatio: '210/297' }}
                            />
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
